/* eslint-disable max-lines -- legacy size; refactor planned in plans/cached-singing-fiddle.md */
import {
  table,
  string,
  boolean,
  number,
  json,
  createSchema,
  createBuilder,
  relationships,
  type ReadonlyJSONObject,
} from "@rocicorp/zero";
import type {
  FieldDefinition,
  FieldValue,
  DefaultSort,
} from "./list-field-types";
import type { CommunitySettings } from "../types/community-settings";
import type { ProfileMetadata } from "../types/profile-metadata";

// Phase 1.5.6 — `users` is now identity-only. The 11 hot-mutating
// columns (typing/presence/status/terminal) moved to `user_presence`
// in PR 1.5.1 and were dropped from this Zero schema in 1.5.6 once
// every reader (server, desktop, iOS) had migrated through PRs 1.5.3
// and 1.5.4.
//
// The columns still exist in the Postgres `users` table — the reverse
// trigger was dropped in 1.5.5, so `users.X` is now stale. PR 1.5.7
// drops them at the DB layer.
//
// Why drop from Zero schema before DB: removing from the client
// schema is what kills IVM fanout through `.related("user")` /
// `.related("sender")` chains for these fields. The DB-level cleanup
// in 1.5.7 is hygiene.
//
// Destructive: clients that connect with the OLD schema (pre-1.5.6
// desktop bundle, pre-1.5.4 iOS) get `SchemaVersionNotSupported`.
// Mitigated by `MIN_DESKTOP_VERSION` / `MIN_IOS_VERSION` env-var
// gating after staged adoption — see
// `.claude/docs/schema-change-playbook.md`.
const users = table("users")
  .columns({
    id: string(),
    email: string(),
    display_name: string(),
    avatar_url: string().optional(),
    headline: string().optional(),
    bio: string().optional(),
    location: string().optional(),
    timezone: string().optional(),
    website_url: string().optional(),
    // Public-community profile extras (social links, "building now", linked
    // GitHub username). Single JSONB blob; NULL/absent = {}. Additive, not
    // hot-mutating. See types/profile-metadata.ts.
    profile_metadata: json<ProfileMetadata>().optional(),
    is_deactivated: boolean(),
    is_guest: boolean(),
    is_coworker: boolean(),
    agent_status_opt_in: boolean(),
    // WS-B1 (Phase 2 multi-region foundation): home region (set at signup
    // from geo-IP via /api/user/bootstrap, WS-B2). Defaults to 'us' on
    // every pre-Phase-2 row.
    home_region: string(),
    // Watermark for cross-region user denormalization (WS-B3). Bumps when
    // the user's profile fields change. Foreign-region replicas use this
    // to decide whether their cached copy is stale.
    profile_updated_at: number(),
    created_at: number(),
    updated_at: number().optional(),
    deleted_at: number().optional(),
  })
  .primaryKey("id");

// user_presence — hot-mutating presence/typing/status/terminal columns
// split out of users so that .related("sender") / .related("user")
// joins don't reverse-scan downstream pipelines on every keystroke.
// See ~/.claude/plans/zero-perf-rollout.md § "Phase 1.5". The hot
// columns ALSO remain on `users` during the migration window (PR 1.5.1
// to PR 1.5.6); they are dropped in the contract step gated by
// MIN_DESKTOP_VERSION + MIN_IOS_VERSION.
const user_presence = table("user_presence")
  .columns({
    user_id: string(),
    typing_in: string().optional(),
    typing_at: number().optional(),
    is_online: boolean(),
    last_active_at: number().optional(),
    status: string().optional(),
    status_emoji: string().optional(),
    status_expiry: number().optional(),
    terminal_process: string().optional(),
    terminal_cwd: string().optional(),
    terminal_active_at: number().optional(),
    terminal_share: boolean(),
  })
  .primaryKey("user_id");

const workspaces = table("workspaces")
  .columns({
    id: string(),
    name: string(),
    slug: string(),
    logo_url: string().optional(),
    access_mode: string(),
    allowed_email_domains: json<string[]>().optional(),
    // Public mode (migration 146). `is_public=true` means any
    // signed-in user with the workspace URL can join via the splash.
    // `public_join_policy` decides between immediate auto-join and
    // admin-approved request-to-join. Independent of `access_mode` —
    // a workspace can be email-domain + public simultaneously, and
    // the email-domain allowlist still filters the public path.
    is_public: boolean(),
    public_join_policy: string().optional(),
    // Public-workspace community settings (migration 154). One owner-tunable
    // JSONB blob; NULL/absent = {}. See types/community-settings.ts.
    community_settings: json<CommunitySettings>().optional(),
    retention_days: number().optional(),
    retention_applies_to_files: boolean(),
    retention_applies_to_pins: boolean(),
    // WS-B1 (Phase 2): authority region for this workspace's data. Set
    // at workspace creation (WS-B13a region selector, geo-IP-defaulted).
    // Immutable without a manual admin migration; self-serve workspace
    // migration is deferred per master plan decision log.
    region: string(),
    created_at: number(),
    created_by: string(),
  })
  .primaryKey("id");

// Public-mode join requests (migration 146). Backs the request-to-join
// admin queue + the requester's "waiting for approval" splash. Server
// inserts the row on a public-workspace request-join POST; mutators
// fire approve/reject from the admin inbox.
const workspace_join_requests = table("workspace_join_requests")
  .columns({
    id: string(),
    workspace_id: string(),
    user_id: string(),
    status: string(),
    message: string().optional(),
    requested_at: number(),
    decided_at: number().optional(),
    decided_by: string().optional(),
  })
  .primaryKey("id");

const workspace_members = table("workspace_members")
  .columns({
    id: string(),
    workspace_id: string(),
    user_id: string(),
    role: string(),
    joined_at: number(),
    removed_at: number().optional(),
    removed_by: string().optional(),
  })
  .primaryKey("id");

const channels = table("channels")
  .columns({
    id: string(),
    workspace_id: string().optional(),
    parent_id: string().optional(),
    name: string(),
    description: string().optional(),
    topic: string().optional(),
    is_private: boolean(),
    is_archived: boolean(),
    posting_restriction: string(),
    retention_days_override: number().optional(),
    type: string().optional(),
    // WS-B1 (Phase 2): channel's authority region. For workspace channels
    // this denormalizes from workspace.region (kept in sync by mutators
    // in WS-B13a). For DMs (workspace_id IS NULL) computed at create time
    // via the lower-userId-wins rule (WS-B4).
    region: string(),
    created_at: number(),
    created_by: string(),
  })
  .primaryKey("id");

const channel_members = table("channel_members")
  .columns({
    id: string(),
    channel_id: string(),
    user_id: string(),
    is_channel_manager: boolean(),
    joined_at: number(),
    muted: boolean(),
    notify: string(),
    hidden: boolean(),
    removed_at: number().optional(),
    removed_by: string().optional(),
  })
  .primaryKey("id");

const messages = table("messages")
  .columns({
    id: string(),
    channel_id: string(),
    user_id: string(),
    sender_display_name: string().optional(),
    sender_avatar_url: string().optional(),
    content: string(),
    thread_id: string().optional(),
    reply_count: number(),
    reply_users: json<string[]>().optional(),
    latest_reply_at: number().optional(),
    // Inline replies (Discord-style): citation FK to the message being
    // replied to. Non-null marks this row as an inline reply that renders
    // in the channel timeline with a quote-pill header. Mutex with
    // thread_id (mutator picks the column at write time).
    replied_to_message_id: string().optional(),
    is_broadcast: boolean(),
    is_edited: boolean(),
    // Phase 7c-D: `mentions` JSONB column dropped in mig 127. Mentions
    // live exclusively in the `message_mentions` junction (relationship
    // declared further below) — read via `.related("message_mentions")`.
    metadata: json<{
      agent?: boolean;
      coworker?: boolean;
      model?: string;
      generated_at?: number;
      error?: boolean;
      /**
       * Pipedream apps the source automation's spec touches —
       * denormalized from `extractAppsUsed(automation.actions)` at
       * insert time so the desktop UI can render small app icons next
       * to the "Automated" badge without a follow-up fetch. Only
       * populated when the message was inserted by the automation
       * engine; chat-driven agent messages omit it.
       */
      apps_used?: Array<{
        slug: string;
        name?: string;
        img_src?: string;
      }>;
      /**
       * Persisted reasoning text for the "Thought process" chip,
       * shown above the message body. Populated by the SSE tee
       * accumulator (`agent-routes.ts`) and `runCoworkerTurn`
       * (`agent-scheduler.ts`) when the model emitted thinking_delta
       * events for this turn. JSONB-only — no dedicated column.
       */
      thinking?: string;
    }>().optional(),
    // NOTE: `agent_thinking` was removed when Tauri DM was unified onto
    // the SSE streaming path. Thinking is now an ephemeral, SSE-driven
    // concern on each client (`agent-dm-stream-store.ts` for Tauri,
    // `AppModel.agentStream*` for iOS) — never persisted to a column.
    // The DB only holds the final message. The column drop is in
    // migrations/079_drop_messages_agent_thinking.sql; the schema hash
    // bump triggers Zero IndexedDB eviction on existing clients.
    //
    // Typed message-model columns (Phase 1 migration + Phase 2+3 writers).
    // Postgres side is in migrations/092_messages_typed_kinds.sql.
    // `kind` is typed as plain string at the Zero layer; the MessageKind
    // union in packages/shared/src/message-payload.ts carries the type
    // safety for TypeScript consumers (ZQL filters work with literal
    // strings). `kind` and `preview_text` are REQUIRED — every writer
    // path in Phase 2+3 stamps both via the shared builder layer.
    kind: string(),
    payload: json<ReadonlyJSONObject>().optional(),
    preview_text: string(),
    calendar_start_at: number().optional(),
    calendar_end_at: number().optional(),
    calendar_title: string().optional(),
    calendar_description: string().optional(),
    email_subject: string().optional(),
    email_body: string().optional(),
    email_from_email: string().optional(),
    terminal_command: string().optional(),
    terminal_output: string().optional(),
    terminal_exit_code: number().optional(),
    call_duration_seconds: number().optional(),
    created_at: number(),
    updated_at: number(),
  })
  .primaryKey("id");

// Phase 1 message-model refactor — side tables for queryable junctions.
// Consumers land in Phase 2+ (calendar-events / email-messages / call-events /
// message-mentions queries + desktop Map selectors + iOS readers).
//
// message_mentions: populated solely by application code as of Phase 7c-C
// (migration 117 dropped the legacy `sync_message_mentions_from_jsonb`
// trigger). Writers: `applyBuiltMessage` / `applyBuiltMessageSql` on
// INSERT, `syncMessageMentionsJunction` on UPDATE-of-mentions edits.
// `messages.mentions` JSONB stays populated by the same writers until
// Phase 7c-D drops the column.
const message_mentions = table("message_mentions")
  .columns({
    message_id: string(),
    user_id: string(),
  })
  .primaryKey("message_id", "user_id");

const calendar_attendees = table("calendar_attendees")
  .columns({
    id: string(),
    message_id: string(),
    user_id: string().optional(),
    email: string().optional(),
    rsvp_status: string().optional(),
  })
  .primaryKey("id");

const email_participants = table("email_participants")
  .columns({
    message_id: string(),
    email: string(),
    role: string(),
  })
  .primaryKey("message_id", "email", "role");

const call_participants = table("call_participants")
  .columns({
    message_id: string(),
    user_id: string(),
    joined_at: number().optional(),
    left_at: number().optional(),
  })
  .primaryKey("message_id", "user_id");

const reactions = table("reactions")
  .columns({
    id: string(),
    message_id: string(),
    user_id: string(),
    emoji: string(),
    created_at: number(),
  })
  .primaryKey("id");

// Phase 2 community curation (migration 155). Per-message, per-user signal
// (helpful / worth_reading / best_answer). Reputation = aggregate of rows.
const community_signals = table("community_signals")
  .columns({
    id: string(),
    workspace_id: string(),
    channel_id: string(),
    message_id: string(),
    user_id: string(),
    signal_type: string(),
    created_at: number(),
  })
  .primaryKey("id");

const link_previews = table("link_previews")
  .columns({
    id: string(),
    message_id: string(),
    url: string(),
    title: string().optional(),
    description: string().optional(),
    image_url: string().optional(),
    favicon_url: string().optional(),
    site_name: string().optional(),
    fetched_at: number(),
    created_at: number(),
  })
  .primaryKey("id");

const attachments = table("attachments")
  .columns({
    id: string(),
    message_id: string(),
    user_id: string(),
    filename: string(),
    file_type: string(),
    file_size: number(),
    file_category: string(),
    storage_key: string(),
    storage_url: string(),
    thumbnail_url: string().optional(),
    width: number().optional(),
    height: number().optional(),
    created_at: number(),
  })
  .primaryKey("id");

const notification_keywords = table("notification_keywords")
  .columns({
    id: string(),
    user_id: string(),
    workspace_id: string(),
    keyword: string(),
    created_at: number(),
  })
  .primaryKey("id");

const device_tokens = table("device_tokens")
  .columns({
    id: string(),
    user_id: string(),
    token: string(),
    platform: string(),
    app_variant: string().optional(),
    created_at: number(),
    updated_at: number(),
    last_seen_at: number(),
  })
  .primaryKey("id");

const web_push_subscriptions = table("web_push_subscriptions")
  .columns({
    id: string(),
    user_id: string(),
    endpoint: string(),
    p256dh: string(),
    auth: string(),
    user_agent: string().optional(),
    created_at: number(),
    updated_at: number(),
    last_seen_at: number(),
  })
  .primaryKey("id");

const notification_events = table("notification_events")
  .columns({
    id: string(),
    recipient_user_id: string(),
    actor_user_id: string(),
    message_id: string().optional(),
    channel_id: string().optional(),
    thread_id: string().optional(),
    workspace_id: string().optional(),
    reason: string(),
    title: string(),
    body: string(),
    payload_json: json<Record<string, string | number | boolean | null>>(),
    created_at: number(),
    read_at: number().optional(),
  })
  .primaryKey("id");

// notification_deliveries removed from Zero schema (server-only table)

const notification_preferences = table("notification_preferences")
  .columns({
    user_id: string(),
    workspace_id: string(),
    global_level: string(),
    email_enabled: boolean(),
    email_delay_minutes: number(),
    desktop_enabled: boolean(),
    mobile_enabled: boolean(),
  })
  .primaryKey("user_id", "workspace_id");

const channel_notification_overrides = table("channel_notification_overrides")
  .columns({
    id: string(),
    user_id: string(),
    channel_id: string(),
    level: string(),
    desktop_level: string().optional(),
    mobile_level: string().optional(),
  })
  .primaryKey("id");

const dnd_settings = table("dnd_settings")
  .columns({
    user_id: string(),
    enabled: boolean(),
    start_time: string().optional(),
    end_time: string().optional(),
    until: number().optional(),
  })
  .primaryKey("user_id");

// Per-user ephemeral UI acknowledgment flags (dismissed onboarding cards,
// onboarded_at, future "seen changelog" / "tour done"). One row per
// (user, flag) so cross-device writes don't clobber. No relationships — read
// directly by user_id. Desktop/web-only (iOS does not query it).
//
// Value is JSON-serializable; the union covers current flag shapes (boolean
// acks, numeric timestamps, string[] sets) and stays open for future ones.
export type UiFlagValue = boolean | number | string | string[] | null;
const user_ui_flags = table("user_ui_flags")
  .columns({
    user_id: string(),
    flag_key: string(),
    value: json<UiFlagValue>().optional(),
    set_at: number(),
  })
  .primaryKey("user_id", "flag_key");

const thread_followers = table("thread_followers")
  .columns({
    id: string(),
    thread_id: string(),
    user_id: string(),
    followed_at: number(),
  })
  .primaryKey("id");

const pins = table("pins")
  .columns({
    id: string(),
    message_id: string(),
    channel_id: string(),
    pinned_by: string(),
    pinned_at: number(),
  })
  .primaryKey("id");

const channel_posting_permissions = table("channel_posting_permissions")
  .columns({
    id: string(),
    channel_id: string(),
    user_id: string(),
    granted_by: string(),
    granted_at: number(),
  })
  .primaryKey("id");

const invites = table("invites")
  .columns({
    id: string(),
    token: string(),
    type: string(),
    creator_id: string().optional(),
    invited_email: string().optional(),
    other_user_id: string().optional(),
    workspace_id: string().optional(),
    channel_id: string().optional(),
    client_space_id: string().optional(),
    max_uses: number().optional(),
    use_count: number(),
    expires_at: number(),
    revoked: boolean(),
    scope: string(),
    created_at: number(),
  })
  .primaryKey("id");

const declined_invites = table("declined_invites")
  .columns({
    id: string(),
    user_id: string(),
    invite_id: string(),
    declined_at: number(),
  })
  .primaryKey("id");

const user_channel_stats = table("user_channel_stats")
  .columns({
    id: string(),
    user_id: string(),
    channel_id: string(),
    unread_count: number(),
    mention_count: number(),
    last_message_at: number().optional(),
    last_read_at: number().optional(),
    updated_at: number(),
  })
  .primaryKey("id");

// mcp_tool_cache removed from Zero schema (migration 101) — server-only
// cache consumed by `server/tool-cache.ts` via direct SQL. It was never
// queried by any client; the two Zero queries (byUser, byUserAndApp)
// that existed were dead code.

// user_automations removed from Zero schema (server-only table)

const workspace_skills = table("workspace_skills")
  .columns({
    id: string(),
    workspace_id: string(),
    created_by: string(),
    name: string(),
    slug: string(),
    description: string().optional(),
    icon: string().optional(),
    prompt: string(),
    allowed_tools: json<string[]>(),
    trigger_type: string(),
    trigger_config: json<ReadonlyJSONObject>(),
    visibility: string(),
    enabled: boolean(),
    category: string(),
    risk_level: string(),
    created_at: number(),
    updated_at: number().optional(),
  })
  .primaryKey("id");

// workspace_mcp_servers removed from Zero schema (server-only table)
// audit_logs removed from Zero schema (server-only table)

const coworkers = table("coworkers")
  .columns({
    id: string(),
    workspace_id: string(),
    created_by: string(),
    role_title: string(),
    expertise: string(),
    personality: string(),
    boundaries: string(),
    custom_instructions: string(),
    model_provider: string(),
    model_id: string(),
    allowed_skill_slugs: json<string[]>().optional(),
    allowed_tool_scope: string(),
    allowed_tools: json<string[]>().optional(),
    enabled: boolean(),
    respond_to_mentions: boolean(),
    respond_to_dms: boolean(),
    mode: string(),
    created_at: number(),
    updated_at: number(),
  })
  .primaryKey("id");

// Junction allowing a space (channels row with type='space') to be
// linked into additional workspaces beyond its host. Renamed from
// client_space_workspace_links + client_space_id → channel_id in
// migration 120.
const channel_workspace_links = table("channel_workspace_links")
  .columns({
    id: string(),
    channel_id: string(),
    workspace_id: string(),
    linked_by: string().optional(),
    role: string(),
    created_at: number(),
  })
  .primaryKey("id");

const lists = table("lists")
  .columns({
    id: string(),
    workspace_id: string(),
    name: string(),
    description: string().optional(),
    icon: string().optional(),
    color: string().optional(),
    prefix: string().optional(),
    template_type: string(),
    field_definitions: json<FieldDefinition[]>(),
    default_sort: json<DefaultSort>().optional(),
    default_group_by: string().optional(),
    created_by: string(),
    created_at: number(),
    updated_at: number(),
  })
  .primaryKey("id");

const list_items = table("list_items")
  .columns({
    id: string(),
    list_id: string(),
    fields: json<Record<string, FieldValue>>(),
    position: number(),
    sequence_number: number().optional(),
    is_archived: boolean(),
    created_by: string(),
    updated_by: string().optional(),
    created_at: number(),
    updated_at: number(),
  })
  .primaryKey("id");

const list_item_comments = table("list_item_comments")
  .columns({
    id: string(),
    list_item_id: string(),
    created_by: string(),
    body: string(),
    created_at: number(),
    modified_at: number(),
  })
  .primaryKey("id");

// -- Relationships --

const userRelationships = relationships(users, ({ one, many }) => ({
  presence: one({
    sourceField: ["id"],
    destSchema: user_presence,
    destField: ["user_id"],
  }),
  workspace_memberships: many({
    sourceField: ["id"],
    destSchema: workspace_members,
    destField: ["user_id"],
  }),
  channel_memberships: many({
    sourceField: ["id"],
    destSchema: channel_members,
    destField: ["user_id"],
  }),
  messages: many({
    sourceField: ["id"],
    destSchema: messages,
    destField: ["user_id"],
  }),
  notification_keywords: many({
    sourceField: ["id"],
    destSchema: notification_keywords,
    destField: ["user_id"],
  }),
  device_tokens: many({
    sourceField: ["id"],
    destSchema: device_tokens,
    destField: ["user_id"],
  }),
  web_push_subscriptions: many({
    sourceField: ["id"],
    destSchema: web_push_subscriptions,
    destField: ["user_id"],
  }),
  notification_events_received: many({
    sourceField: ["id"],
    destSchema: notification_events,
    destField: ["recipient_user_id"],
  }),
  notification_events_acted: many({
    sourceField: ["id"],
    destSchema: notification_events,
    destField: ["actor_user_id"],
  }),
  notification_preferences: many({
    sourceField: ["id"],
    destSchema: notification_preferences,
    destField: ["user_id"],
  }),
  dnd_settings: many({
    sourceField: ["id"],
    destSchema: dnd_settings,
    destField: ["user_id"],
  }),
  channel_notification_overrides: many({
    sourceField: ["id"],
    destSchema: channel_notification_overrides,
    destField: ["user_id"],
  }),
  thread_follows: many({
    sourceField: ["id"],
    destSchema: thread_followers,
    destField: ["user_id"],
  }),
  channel_stats: many({
    sourceField: ["id"],
    destSchema: user_channel_stats,
    destField: ["user_id"],
  }),
  coworker: many({
    sourceField: ["id"],
    destSchema: coworkers,
    destField: ["id"],
  }),
}));

// Reverse one-to-one back from user_presence to users so queries on
// user_presence can chain `.related("user")` to pull identity fields.
const userPresenceRelationships = relationships(user_presence, ({ one }) => ({
  user: one({
    sourceField: ["user_id"],
    destSchema: users,
    destField: ["id"],
  }),
}));

const workspaceRelationships = relationships(workspaces, ({ many }) => ({
  members: many({
    sourceField: ["id"],
    destSchema: workspace_members,
    destField: ["workspace_id"],
  }),
  channels: many({
    sourceField: ["id"],
    destSchema: channels,
    destField: ["workspace_id"],
  }),
  notification_keywords: many({
    sourceField: ["id"],
    destSchema: notification_keywords,
    destField: ["workspace_id"],
  }),
  notification_events: many({
    sourceField: ["id"],
    destSchema: notification_events,
    destField: ["workspace_id"],
  }),
  notification_preferences: many({
    sourceField: ["id"],
    destSchema: notification_preferences,
    destField: ["workspace_id"],
  }),
  lists: many({
    sourceField: ["id"],
    destSchema: lists,
    destField: ["workspace_id"],
  }),
  coworkers: many({
    sourceField: ["id"],
    destSchema: coworkers,
    destField: ["workspace_id"],
  }),
  space_links: many({
    sourceField: ["id"],
    destSchema: channel_workspace_links,
    destField: ["workspace_id"],
  }),
  // Phase 4 relationship — workspace's owned skills/agents. Powers
  // `workspaces.byIdComposite` (PR 4.2): a single subscription returns
  // the workspace + members + coworkers + channels + skills, replacing
  // the cross-surface fan-out (TeamView, CoworkersView,
  // WorkspaceMembersDialog all subscribe to overlapping data today).
  workspace_skills: many({
    sourceField: ["id"],
    destSchema: workspace_skills,
    destField: ["workspace_id"],
  }),
  // Pending/decided public-mode join requests targeting this workspace.
  // Powers the admin queue inside TeamView + access-tab.
  join_requests: many({
    sourceField: ["id"],
    destSchema: workspace_join_requests,
    destField: ["workspace_id"],
  }),
}));

const workspaceJoinRequestRelationships = relationships(
  workspace_join_requests,
  ({ one }) => ({
    workspace: one({
      sourceField: ["workspace_id"],
      destSchema: workspaces,
      destField: ["id"],
    }),
    user: one({
      sourceField: ["user_id"],
      destSchema: users,
      destField: ["id"],
    }),
    decided_by_user: one({
      sourceField: ["decided_by"],
      destSchema: users,
      destField: ["id"],
    }),
  }),
);

const channelRelationships = relationships(channels, ({ one, many }) => ({
  workspace: one({
    sourceField: ["workspace_id"],
    destSchema: workspaces,
    destField: ["id"],
  }),
  parent: one({
    sourceField: ["parent_id"],
    destSchema: channels,
    destField: ["id"],
  }),
  child_channels: many({
    sourceField: ["id"],
    destSchema: channels,
    destField: ["parent_id"],
  }),
  workspace_links: many({
    sourceField: ["id"],
    destSchema: channel_workspace_links,
    destField: ["channel_id"],
  }),
  // Used by spacesQueries.byIdWithRelated to render the creator on the
  // space settings dialog. Defined here (not just on the legacy
  // client_spaces table) because spaces are now channels.
  creator: one({
    sourceField: ["created_by"],
    destSchema: users,
    destField: ["id"],
  }),
  // Invites that target this channel as a space (post migration 120).
  // The FK column on `invites` is preserved as `client_space_id` per
  // the deferred-rename plan; only the relationship target changed.
  invite_links: many({
    sourceField: ["id"],
    destSchema: invites,
    destField: ["client_space_id"],
  }),
  members: many({
    sourceField: ["id"],
    destSchema: channel_members,
    destField: ["channel_id"],
  }),
  messages: many({
    sourceField: ["id"],
    destSchema: messages,
    destField: ["channel_id"],
  }),
  pins: many({
    sourceField: ["id"],
    destSchema: pins,
    destField: ["channel_id"],
  }),
  posting_permissions: many({
    sourceField: ["id"],
    destSchema: channel_posting_permissions,
    destField: ["channel_id"],
  }),
  notification_events: many({
    sourceField: ["id"],
    destSchema: notification_events,
    destField: ["channel_id"],
  }),
  notification_overrides: many({
    sourceField: ["id"],
    destSchema: channel_notification_overrides,
    destField: ["channel_id"],
  }),
  // Per-channel user state (last_read_at, unread_count, mention_count, ...).
  // Added in P1 of the channel_members.last_read_at split refactor.
  // Used by Phase 4b's `notify`-style whereExists chains and by sidebar
  // selectors that need to project ucs columns through the channel relationship.
  userChannelStats: many({
    sourceField: ["id"],
    destSchema: user_channel_stats,
    destField: ["channel_id"],
  }),
}));

const messageRelationships = relationships(messages, ({ one, many }) => ({
  sender: one({
    sourceField: ["user_id"],
    destSchema: users,
    destField: ["id"],
  }),
  channel: one({
    sourceField: ["channel_id"],
    destSchema: channels,
    destField: ["id"],
  }),
  // Phase 3 self-referential relationship — messages whose `thread_id`
  // points at this message. Powers `byThreadComposite` (PR 3.2):
  // a single subscription returns the thread parent + all replies + their
  // sender / reactions / etc., replacing the 3 fan-out queries
  // ThreadPanel currently runs (parent + replies + thread_followers).
  // See https://zero.rocicorp.dev/docs/schema#relationships for the
  // documented self-ref pattern (`message.replies`).
  thread_replies: many({
    sourceField: ["id"],
    destSchema: messages,
    destField: ["thread_id"],
  }),
  // Discord-shape inline-reply citation source — when this row has
  // `replied_to_message_id` set, `quoted_source` resolves to the cited
  // message. Powers the citation pill in MessageBody for surfaces that
  // don't have access to a per-channel `messageById` map (notably
  // ThreadPanel rendering an inline-reply parent — see
  // `byThreadComposite`'s `.related("quoted_source", …)` chain).
  // Returns null when `replied_to_message_id IS NULL` or the source
  // has been deleted (FK is `ON DELETE SET NULL`, mig 130).
  quoted_source: one({
    sourceField: ["replied_to_message_id"],
    destSchema: messages,
    destField: ["id"],
  }),
  reactions: many({
    sourceField: ["id"],
    destSchema: reactions,
    destField: ["message_id"],
  }),
  // Phase 2 community curation — helpful / worth_reading / best_answer marks.
  // Rides the composite channel query via `.related("community_signals")`
  // (same shape as reactions); empty for messages no one has signaled.
  community_signals: many({
    sourceField: ["id"],
    destSchema: community_signals,
    destField: ["message_id"],
  }),
  link_previews: many({
    sourceField: ["id"],
    destSchema: link_previews,
    destField: ["message_id"],
  }),
  attachments: many({
    sourceField: ["id"],
    destSchema: attachments,
    destField: ["message_id"],
  }),
  thread_followers: many({
    sourceField: ["id"],
    destSchema: thread_followers,
    destField: ["thread_id"],
  }),
  notification_events: many({
    sourceField: ["id"],
    destSchema: notification_events,
    destField: ["message_id"],
  }),
  // Phase 1 staged relationships — consumers in Phase 2+ queries.
  message_mentions: many({
    sourceField: ["id"],
    destSchema: message_mentions,
    destField: ["message_id"],
  }),
  calendar_attendees: many({
    sourceField: ["id"],
    destSchema: calendar_attendees,
    destField: ["message_id"],
  }),
  email_participants: many({
    sourceField: ["id"],
    destSchema: email_participants,
    destField: ["message_id"],
  }),
  call_participants: many({
    sourceField: ["id"],
    destSchema: call_participants,
    destField: ["message_id"],
  }),
}));

// Phase 1 side-table relationships. `message` and (where applicable) `user` backlinks
// so ZQL `.whereExists('message', ...)` + `.whereExists('user', ...)` compose the
// two-hop channel-membership guard established by auxiliary.ts for reactions /
// attachments / link_previews.
const messageMentionRelationships = relationships(
  message_mentions,
  ({ one }) => ({
    message: one({
      sourceField: ["message_id"],
      destSchema: messages,
      destField: ["id"],
    }),
    user: one({
      sourceField: ["user_id"],
      destSchema: users,
      destField: ["id"],
    }),
  }),
);

const calendarAttendeeRelationships = relationships(
  calendar_attendees,
  ({ one }) => ({
    message: one({
      sourceField: ["message_id"],
      destSchema: messages,
      destField: ["id"],
    }),
    user: one({
      sourceField: ["user_id"],
      destSchema: users,
      destField: ["id"],
    }),
  }),
);

const emailParticipantRelationships = relationships(
  email_participants,
  ({ one }) => ({
    message: one({
      sourceField: ["message_id"],
      destSchema: messages,
      destField: ["id"],
    }),
  }),
);

const callParticipantRelationships = relationships(
  call_participants,
  ({ one }) => ({
    message: one({
      sourceField: ["message_id"],
      destSchema: messages,
      destField: ["id"],
    }),
    user: one({
      sourceField: ["user_id"],
      destSchema: users,
      destField: ["id"],
    }),
  }),
);

const workspaceMemberRelationships = relationships(
  workspace_members,
  ({ one }) => ({
    workspace: one({
      sourceField: ["workspace_id"],
      destSchema: workspaces,
      destField: ["id"],
    }),
    user: one({
      sourceField: ["user_id"],
      destSchema: users,
      destField: ["id"],
    }),
  }),
);

const channelMemberRelationships = relationships(
  channel_members,
  ({ one }) => ({
    channel: one({
      sourceField: ["channel_id"],
      destSchema: channels,
      destField: ["id"],
    }),
    user: one({
      sourceField: ["user_id"],
      destSchema: users,
      destField: ["id"],
    }),
    // Composite-key relationship to user_channel_stats. Lets callers do
    // `channel_members.byUser().related("stats")` to co-load a user's
    // per-channel stat row (unread/mention/last_message_at) in one query.
    // First composite-key relationship in this repo (per zero-schema.md
    // rules: "sourceField and destField are arrays (for composite keys)").
    stats: one({
      sourceField: ["channel_id", "user_id"],
      destSchema: user_channel_stats,
      destField: ["channel_id", "user_id"],
    }),
  }),
);

const reactionRelationships = relationships(reactions, ({ one }) => ({
  message: one({
    sourceField: ["message_id"],
    destSchema: messages,
    destField: ["id"],
  }),
  user: one({
    sourceField: ["user_id"],
    destSchema: users,
    destField: ["id"],
  }),
}));

const community_signalsRelationships = relationships(
  community_signals,
  ({ one }) => ({
    channel: one({
      sourceField: ["channel_id"],
      destSchema: channels,
      destField: ["id"],
    }),
    user: one({
      sourceField: ["user_id"],
      destSchema: users,
      destField: ["id"],
    }),
  }),
);

const linkPreviewRelationships = relationships(link_previews, ({ one }) => ({
  message: one({
    sourceField: ["message_id"],
    destSchema: messages,
    destField: ["id"],
  }),
}));

const attachmentRelationships = relationships(attachments, ({ one }) => ({
  message: one({
    sourceField: ["message_id"],
    destSchema: messages,
    destField: ["id"],
  }),
  uploader: one({
    sourceField: ["user_id"],
    destSchema: users,
    destField: ["id"],
  }),
}));

const notificationKeywordRelationships = relationships(
  notification_keywords,
  ({ one }) => ({
    user: one({
      sourceField: ["user_id"],
      destSchema: users,
      destField: ["id"],
    }),
    workspace: one({
      sourceField: ["workspace_id"],
      destSchema: workspaces,
      destField: ["id"],
    }),
  }),
);

const deviceTokenRelationships = relationships(device_tokens, ({ one }) => ({
  user: one({
    sourceField: ["user_id"],
    destSchema: users,
    destField: ["id"],
  }),
}));

const webPushSubscriptionRelationships = relationships(
  web_push_subscriptions,
  ({ one }) => ({
    user: one({
      sourceField: ["user_id"],
      destSchema: users,
      destField: ["id"],
    }),
  }),
);

const notificationEventRelationships = relationships(
  notification_events,
  ({ one }) => ({
    recipient: one({
      sourceField: ["recipient_user_id"],
      destSchema: users,
      destField: ["id"],
    }),
    actor: one({
      sourceField: ["actor_user_id"],
      destSchema: users,
      destField: ["id"],
    }),
    message: one({
      sourceField: ["message_id"],
      destSchema: messages,
      destField: ["id"],
    }),
    channel: one({
      sourceField: ["channel_id"],
      destSchema: channels,
      destField: ["id"],
    }),
    workspace: one({
      sourceField: ["workspace_id"],
      destSchema: workspaces,
      destField: ["id"],
    }),
  }),
);

const notificationPreferenceRelationships = relationships(
  notification_preferences,
  ({ one }) => ({
    user: one({
      sourceField: ["user_id"],
      destSchema: users,
      destField: ["id"],
    }),
    workspace: one({
      sourceField: ["workspace_id"],
      destSchema: workspaces,
      destField: ["id"],
    }),
  }),
);

const channelNotificationOverrideRelationships = relationships(
  channel_notification_overrides,
  ({ one }) => ({
    user: one({
      sourceField: ["user_id"],
      destSchema: users,
      destField: ["id"],
    }),
    channel: one({
      sourceField: ["channel_id"],
      destSchema: channels,
      destField: ["id"],
    }),
  }),
);

const dndSettingsRelationships = relationships(dnd_settings, ({ one }) => ({
  user: one({
    sourceField: ["user_id"],
    destSchema: users,
    destField: ["id"],
  }),
}));

const threadFollowerRelationships = relationships(
  thread_followers,
  ({ one }) => ({
    thread: one({
      sourceField: ["thread_id"],
      destSchema: messages,
      destField: ["id"],
    }),
    user: one({
      sourceField: ["user_id"],
      destSchema: users,
      destField: ["id"],
    }),
  }),
);

const pinRelationships = relationships(pins, ({ one }) => ({
  message: one({
    sourceField: ["message_id"],
    destSchema: messages,
    destField: ["id"],
  }),
  channel: one({
    sourceField: ["channel_id"],
    destSchema: channels,
    destField: ["id"],
  }),
  pinner: one({
    sourceField: ["pinned_by"],
    destSchema: users,
    destField: ["id"],
  }),
}));

const channelPostingPermissionRelationships = relationships(
  channel_posting_permissions,
  ({ one }) => ({
    channel: one({
      sourceField: ["channel_id"],
      destSchema: channels,
      destField: ["id"],
    }),
    user: one({
      sourceField: ["user_id"],
      destSchema: users,
      destField: ["id"],
    }),
    granter: one({
      sourceField: ["granted_by"],
      destSchema: users,
      destField: ["id"],
    }),
  }),
);

const inviteRelationships = relationships(invites, ({ one, many }) => ({
  workspace: one({
    sourceField: ["workspace_id"],
    destSchema: workspaces,
    destField: ["id"],
  }),
  creator: one({
    sourceField: ["creator_id"],
    destSchema: users,
    destField: ["id"],
  }),
  channel: one({
    sourceField: ["channel_id"],
    destSchema: channels,
    destField: ["id"],
  }),
  // FK column `client_space_id` is preserved on the invites table; the
  // relationship target now points at channels (post migration 120 the
  // FK target is channels(id) for rows with type='space'). Cosmetic
  // column-name rename → `parent_channel_id` deferred to follow-up.
  parent_channel: one({
    sourceField: ["client_space_id"],
    destSchema: channels,
    destField: ["id"],
  }),
  other_user: one({
    sourceField: ["other_user_id"],
    destSchema: users,
    destField: ["id"],
  }),
  // Resolves the invite's `invited_email` to a `users` row by matching
  // email. Used by `invites.receivedByMe` to find invites addressed to
  // the current user via email (the common case — most invites are
  // email-only, with `other_user_id` NULL because the recipient hadn't
  // signed up yet or the inviter only knew their email).
  //
  // Non-PK destField (`email`) — Zero supports this (zbugs uses non-PK
  // destFields extensively). The join is strict-equality, which is safe
  // because BOTH sides are enforced lowercase: `users.email` by migration
  // 147's `users_email_lowercase` CHECK, and `invites.invited_email` by
  // migration 148's BEFORE INSERT/UPDATE trigger (+ `inviteInsertCore`
  // normalizing on the mutator path). Without both, a mixed-case invite
  // email would be invisible to its recipient in `receivedByMe`.
  invited_user_by_email: one({
    sourceField: ["invited_email"],
    destSchema: users,
    destField: ["email"],
  }),
  // Decline records for this invite (one per user who declined). Used by
  // `invites.receivedByMe` to exclude invites the current user has
  // explicitly declined — `not(exists("declines", dq => dq.where("user_id", sub)))`.
  declines: many({
    sourceField: ["id"],
    destSchema: declined_invites,
    destField: ["invite_id"],
  }),
}));

const declinedInviteRelationships = relationships(
  declined_invites,
  ({ one }) => ({
    user: one({
      sourceField: ["user_id"],
      destSchema: users,
      destField: ["id"],
    }),
    invite: one({
      sourceField: ["invite_id"],
      destSchema: invites,
      destField: ["id"],
    }),
  }),
);

const userChannelStatsRelationships = relationships(
  user_channel_stats,
  ({ one }) => ({
    user: one({
      sourceField: ["user_id"],
      destSchema: users,
      destField: ["id"],
    }),
    channel: one({
      sourceField: ["channel_id"],
      destSchema: channels,
      destField: ["id"],
    }),
  }),
);

// -- Integration relationships --

const workspaceSkillRelationships = relationships(
  workspace_skills,
  ({ one }) => ({
    workspace: one({
      sourceField: ["workspace_id"],
      destSchema: workspaces,
      destField: ["id"],
    }),
    creator: one({
      sourceField: ["created_by"],
      destSchema: users,
      destField: ["id"],
    }),
  }),
);

// -- Lists relationships --

const listRelationships = relationships(lists, ({ one, many }) => ({
  workspace: one({
    sourceField: ["workspace_id"],
    destSchema: workspaces,
    destField: ["id"],
  }),
  creator: one({
    sourceField: ["created_by"],
    destSchema: users,
    destField: ["id"],
  }),
  items: many({
    sourceField: ["id"],
    destSchema: list_items,
    destField: ["list_id"],
  }),
}));

const listItemRelationships = relationships(list_items, ({ one, many }) => ({
  list: one({
    sourceField: ["list_id"],
    destSchema: lists,
    destField: ["id"],
  }),
  creator: one({
    sourceField: ["created_by"],
    destSchema: users,
    destField: ["id"],
  }),
  updater: one({
    sourceField: ["updated_by"],
    destSchema: users,
    destField: ["id"],
  }),
  comments: many({
    sourceField: ["id"],
    destSchema: list_item_comments,
    destField: ["list_item_id"],
  }),
}));

const listItemCommentRelationships = relationships(
  list_item_comments,
  ({ one }) => ({
    item: one({
      sourceField: ["list_item_id"],
      destSchema: list_items,
      destField: ["id"],
    }),
    creator: one({
      sourceField: ["created_by"],
      destSchema: users,
      destField: ["id"],
    }),
  }),
);

const coworkerRelationships = relationships(coworkers, ({ one }) => ({
  user: one({
    sourceField: ["id"],
    destSchema: users,
    destField: ["id"],
  }),
  workspace: one({
    sourceField: ["workspace_id"],
    destSchema: workspaces,
    destField: ["id"],
  }),
  creator: one({
    sourceField: ["created_by"],
    destSchema: users,
    destField: ["id"],
  }),
}));

const channelWorkspaceLinkRelationships = relationships(
  channel_workspace_links,
  ({ one }) => ({
    channel: one({
      sourceField: ["channel_id"],
      destSchema: channels,
      destField: ["id"],
    }),
    workspace: one({
      sourceField: ["workspace_id"],
      destSchema: workspaces,
      destField: ["id"],
    }),
    linked_by_user: one({
      sourceField: ["linked_by"],
      destSchema: users,
      destField: ["id"],
    }),
  }),
);

export const schema = createSchema({
  tables: [
    users,
    user_presence,
    workspaces,
    workspace_members,
    channels,
    channel_members,
    messages,
    reactions,
    community_signals,
    link_previews,
    attachments,
    notification_keywords,
    device_tokens,
    web_push_subscriptions,
    notification_events,
    notification_preferences,
    channel_notification_overrides,
    dnd_settings,
    user_ui_flags,
    thread_followers,
    pins,
    channel_posting_permissions,
    invites,
    declined_invites,
    user_channel_stats,
    coworkers,
    channel_workspace_links,
    workspace_skills,
    lists,
    list_items,
    list_item_comments,
    // Phase 1 message-model refactor — staged for Phase 2+ consumers.
    message_mentions,
    calendar_attendees,
    email_participants,
    call_participants,
    workspace_join_requests,
  ],
  relationships: [
    userRelationships,
    userPresenceRelationships,
    workspaceRelationships,
    workspaceMemberRelationships,
    channelRelationships,
    channelMemberRelationships,
    messageRelationships,
    reactionRelationships,
    community_signalsRelationships,
    linkPreviewRelationships,
    attachmentRelationships,
    notificationKeywordRelationships,
    deviceTokenRelationships,
    webPushSubscriptionRelationships,
    notificationEventRelationships,
    notificationPreferenceRelationships,
    channelNotificationOverrideRelationships,
    dndSettingsRelationships,
    threadFollowerRelationships,
    pinRelationships,
    channelPostingPermissionRelationships,
    inviteRelationships,
    declinedInviteRelationships,
    userChannelStatsRelationships,
    coworkerRelationships,
    channelWorkspaceLinkRelationships,
    workspaceSkillRelationships,
    listRelationships,
    listItemRelationships,
    listItemCommentRelationships,
    messageMentionRelationships,
    calendarAttendeeRelationships,
    emailParticipantRelationships,
    callParticipantRelationships,
    workspaceJoinRequestRelationships,
  ],
});

export type Schema = typeof schema;
export const zql = createBuilder(schema);

declare module "@rocicorp/zero" {
  interface DefaultTypes {
    // `readonly` is REQUIRED — see ../zero-context.ts for full reasoning.
    readonly schema: Schema;
  }
}
